import type { Game } from "@tabletop/shared";

/**
 * Display helpers.
 *
 * Pure and framework-free on purpose: this is the part of the app that can be
 * tested without a React Native renderer.
 *
 * The parameters are structural rather than `Pick<Game, ...>`. A database row
 * has `minPlayers: number | null`; an API response has it optional, because
 * a field can be absent from JSON as well as null. Requiring the stricter
 * shape would force every screen to reshape a response before it could format
 * it. The contract link is kept by the assertion at the bottom instead.
 */

type Nullish<T> = T | null | undefined;

export interface DisplayGame {
  title: string;
  yearPublished?: Nullish<number>;
  designers?: Nullish<string[]>;
  minPlayers?: Nullish<number>;
  maxPlayers?: Nullish<number>;
  playingTimeMinutes?: Nullish<number>;
}

export function formatPlayerCount(
  game: Pick<DisplayGame, "minPlayers" | "maxPlayers">,
): string {
  const min = game.minPlayers ?? null;
  const max = game.maxPlayers ?? null;
  if (min === null && max === null) return "Unknown";
  if (min !== null && max === null) return `${min}+`;
  if (min === null && max !== null) return `Up to ${max}`;
  if (min === max) return `${min}`;
  return `${min}–${max}`;
}

export function formatPlayingTime(game: Pick<DisplayGame, "playingTimeMinutes">): string {
  const minutes = game.playingTimeMinutes ?? null;
  if (minutes === null) return "Unknown";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

export function formatDesigners(game: Pick<DisplayGame, "designers">, limit = 2): string {
  const designers = game.designers ?? [];
  if (designers.length === 0) return "Unknown designer";
  if (designers.length <= limit) return designers.join(", ");
  // "and 2 others" rather than a truncated list -- a name cut in half reads
  // like bad data rather than like a deliberate summary.
  return `${designers.slice(0, limit).join(", ")} and ${designers.length - limit} others`;
}

export function formatSubtitle(game: DisplayGame): string {
  return [
    game.yearPublished == null ? null : String(game.yearPublished),
    formatPlayerCount(game),
    formatPlayingTime(game),
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/**
 * The contract link, enforced at compile time.
 *
 * A `Game` from `packages/shared` must always be formattable. If the shared
 * contract changes shape -- a column renamed, a type narrowed -- this stops
 * compiling, which is the check WI-023 existed to establish and the reason
 * these helpers are not typed loosely as `Record<string, unknown>`.
 */
const _contract: DisplayGame = null as unknown as Game;
void _contract;
